import { analytics } from '../analytics';
import { getCommonAnalytics } from '../common-analytics';
import { getGoogleAnalytics, sendLegacyGA } from '../utils/legacy-ga';

const royaltyDropdownItemSelector = '.dropdown.multiple-purchase-option-dropdown .dropdown-menu .dropdown-item';
const royaltyDropdownToggleSelector = '.multiple-purchase-option-dropdown .dropdown-toggle';

function findRoyaltyDropdownItem(target: EventTarget | null): Element | null {
  return target instanceof Element ? target.closest(royaltyDropdownItemSelector) : null;
}

function toggleId(toggle: Element): string | undefined {
  const el = toggle as HTMLElement;
  return el.id || el.closest('[id]')?.id;
}

// Shared "HE PDP Purchase Option Dropdown" toggle payload, distinct from the item-selection
// click payload below; only `expanded` differs between the show/hide rules.
function toggleAnalyticsFields(expanded: boolean) {
  return {
    label: 'HE PDP Purchase Option Dropdown',
    url: () => window.location.href,
    eventName: 'purchase-options-dropdown',
    comp: 'mhecomm-purchase-options',
    id: ({ element }: { element: Element }) => toggleId(element),
    area: 'main',
    expanded,
  };
}

// Faithful port of pdp/ts/royalty-dropdown-analytics.ts (mh-aem): separate click + keydown
// (Space/Enter) bindings, matching the legacy dual-listener behavior on non-mh-emit dropdown items.
export function registerRoyaltyDropdownAnalytics(target: Document = document): void {
  // Item selection click: built with the shared fluent analytics rule builder (see
  // src/common/datalayer/analytics.ts) instead of a hand-rolled listener.
  analytics(royaltyDropdownItemSelector)
    .name('royalty-dropdown-item-click')
    .on('click')
    .collectCommon()
    .enrich(({ element }) => {
      sendLegacyGA(getGoogleAnalytics(element));
    })
    .emit();

  // Keydown stays a manual listener: the fluent API has no per-rule key filter, and this
  // must only fire for Space/Enter, not every keydown bubbling through a dropdown item.
  target.addEventListener('keydown', (event: Event) => {
    const keyboardEvent = event as KeyboardEvent;
    if (keyboardEvent.key !== ' ' && keyboardEvent.key !== 'Enter') return;
    const dropdownItem = findRoyaltyDropdownItem(event.target);
    if (dropdownItem) {
      const data = getCommonAnalytics(dropdownItem);
      window.Mhecomm?.emit('click', data, {}, dropdownItem);
      sendLegacyGA(getGoogleAnalytics(dropdownItem));
    }
  });

  // Bootstrap dropdown show/hide events fire on the toggle element and bubble.
  analytics(royaltyDropdownToggleSelector)
    .name('royalty-dropdown-toggle-show')
    .on('show.bs.dropdown')
    .set(toggleAnalyticsFields(true))
    .emit('click');

  analytics(royaltyDropdownToggleSelector)
    .name('royalty-dropdown-toggle-hide')
    .on('hide.bs.dropdown')
    .set(toggleAnalyticsFields(false))
    .emit('click');
}
